import React, { useMemo, useState, Fragment } from 'react';
import { SearchIcon, FilterIcon, CheckIcon, AlertCircleIcon, XIcon, ChevronDownIcon, ChevronUpIcon, AlertTriangleIcon, RefreshCwIcon, LoaderIcon, ShieldCheckIcon } from 'lucide-react';
import { Lead, RoutingStatus, ErrorCode } from '../types/leads';
interface AllLeadsTableProps {
  leads: Lead[];
  availableSources: string[];
  onPushSingle: (id: string) => void;
}
type SortField = 'name' | 'leadSource' | 'createdAt' | 'status' | 'errorCode';
type SortDirection = 'asc' | 'desc';
const ERROR_TYPE_LABELS: Record<ErrorCode, string> = {
  SF_API_TIMEOUT: 'API Timeout',
  INVALID_PHONE: 'Invalid Phone',
  DUPLICATE_LEAD: 'Duplicate Lead',
  LIST_NOT_FOUND: 'List Not Found',
  AUTH_EXPIRED: 'Auth Expired',
  FIELD_MAPPING: 'Field Mapping',
  RATE_LIMITED: 'Rate Limited'
};
export function AllLeadsTable({
  leads,
  availableSources,
  onPushSingle
}: AllLeadsTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RoutingStatus | null>(null);
  const [errorFilter, setErrorFilter] = useState<ErrorCode | null>(null);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedAndFilteredLeads = useMemo(() => {
    // First filter
    const filtered = leads.filter(lead => {
      if (sourceFilter && lead.leadSource !== sourceFilter) return false;
      if (statusFilter && lead.status !== statusFilter) return false;
      if (errorFilter && lead.errorCode !== errorFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return lead.name.toLowerCase().includes(query) || lead.email.toLowerCase().includes(query) || lead.phone.includes(query);
      }
      return true;
    });
    // Then sort
    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'leadSource':
          comparison = a.leadSource.localeCompare(b.leadSource);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'errorCode':
          const codeA = a.errorCode || '';
          const codeB = b.errorCode || '';
          comparison = codeA.localeCompare(codeB);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [leads, sourceFilter, statusFilter, errorFilter, searchQuery, sortField, sortDirection]);
  const getStatusBadge = (lead: Lead) => {
    switch (lead.status) {
      case 'routed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1">
            Routed
          </span>;
      case 'resolved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2">
            Resolved
          </span>;
      case 'pending_verification':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse" data-id="element-3">
            <LoaderIcon className="h-3 w-3 mr-1 animate-spin" data-id="element-4" />
            Verifying
          </span>;
      case 'verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5">
            <ShieldCheckIcon className="h-3 w-3 mr-1" data-id="element-6" />
            Verified
          </span>;
      case 'missing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-7">
            Missing
          </span>;
      case 'error':
        const isExpanded = expandedErrorId === lead.id;
        return <button onClick={e => {
          e.stopPropagation();
          setExpandedErrorId(isExpanded ? null : lead.id);
        }} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1" aria-expanded={isExpanded} data-id="element-8">
            Error
            <ChevronDownIcon className={`ml-1 h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-9" />
          </button>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-10">
            Pending
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11">
            {lead.status}
          </span>;
    }
  };
  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-3 w-3 inline" data-id="element-12" /> : <ChevronDownIcon className="ml-1 h-3 w-3 inline" data-id="element-13" />;
  };
  const SortableHeader = ({
    field,
    label,
    className = ''
  }: {
    field: SortField;
    label: string;
    className?: string;
  }) => <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${sortField === field ? 'text-gray-900 font-semibold' : 'text-gray-500'} ${className}`} onClick={() => handleSort(field)} data-id="element-14">
      <div className="flex items-center" data-id="element-15">
        {label}
        <SortIcon field={field} data-id="element-16" />
      </div>
    </th>;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-17">
      {/* Header & Toolbar */}
      <div className="p-5 border-b border-gray-200 bg-white" data-id="element-18">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-19">
          <div className="flex items-center" data-id="element-20">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-21">
              All Leads Created Today
            </h2>
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-22">
              {sortedAndFilteredLeads.length} Leads
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3" data-id="element-23">
          {/* Search */}
          <div className="relative flex-1" data-id="element-24">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-25">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-26" />
            </div>
            <input type="text" placeholder="Search by name, email, or phone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-27" />
          </div>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap sm:flex-nowrap" data-id="element-28">
            <div className="relative w-full sm:w-auto" data-id="element-29">
              <select value={sourceFilter || ''} onChange={e => setSourceFilter(e.target.value || null)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-30">
                <option value="" data-id="element-31">All Sources</option>
                {availableSources.map(s => <option key={s} value={s} data-id="element-32">
                    {s}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-33">
                <FilterIcon className="h-4 w-4" data-id="element-34" />
              </div>
            </div>

            <div className="relative w-full sm:w-auto" data-id="element-35">
              <select value={statusFilter || ''} onChange={e => {
              setStatusFilter(e.target.value as RoutingStatus || null);
              if (e.target.value !== 'error') setErrorFilter(null);
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-36">
                <option value="" data-id="element-37">All Statuses</option>
                <option value="routed" data-id="element-38">Routed</option>
                <option value="missing" data-id="element-39">Missing</option>
                <option value="error" data-id="element-40">Error</option>
                <option value="pending" data-id="element-41">Pending</option>
                <option value="pending_verification" data-id="element-42">Verifying</option>
                <option value="verified" data-id="element-43">Verified</option>
                <option value="resolved" data-id="element-44">Resolved</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-45">
                <FilterIcon className="h-4 w-4" data-id="element-46" />
              </div>
            </div>

            <div className="relative w-full sm:w-auto" data-id="element-47">
              <select value={errorFilter || ''} onChange={e => {
              setErrorFilter(e.target.value as ErrorCode || null);
              if (e.target.value) setStatusFilter('error');
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-48">
                <option value="" data-id="element-49">All Error Types</option>
                {Object.entries(ERROR_TYPE_LABELS).map(([code, label]) => <option key={code} value={code} data-id="element-50">
                    {label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-51">
                <AlertTriangleIcon className="h-4 w-4" data-id="element-52" />
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Chips */}
        {(sourceFilter || statusFilter || errorFilter || searchQuery) && <div className="flex items-center gap-2 mt-4 flex-wrap" data-id="element-53">
            <span className="text-xs text-gray-500 font-medium" data-id="element-54">
              Active Filters:
            </span>
            {sourceFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-55">
                Source: {sourceFilter}
                <button type="button" onClick={() => setSourceFilter(null)} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-56">
                  <XIcon className="h-3 w-3" data-id="element-57" />
                </button>
              </span>}
            {statusFilter && !errorFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-58">
                Status: {statusFilter}
                <button type="button" onClick={() => setStatusFilter(null)} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-59">
                  <XIcon className="h-3 w-3" data-id="element-60" />
                </button>
              </span>}
            {errorFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-61">
                Error: {ERROR_TYPE_LABELS[errorFilter]}
                <button type="button" onClick={() => setErrorFilter(null)} className="ml-1.5 inline-flex text-amber-600 hover:text-amber-800" data-id="element-62">
                  <XIcon className="h-3 w-3" data-id="element-63" />
                </button>
              </span>}
            {searchQuery && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-64">
                Search: "{searchQuery}"
                <button type="button" onClick={() => setSearchQuery('')} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-65">
                  <XIcon className="h-3 w-3" data-id="element-66" />
                </button>
              </span>}
          </div>}
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-67">
        {sortedAndFilteredLeads.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-68">
            <thead className="bg-gray-50" data-id="element-69">
              <tr data-id="element-70">
                <SortableHeader field="name" label="Lead Details" data-id="element-71" />
                <SortableHeader field="leadSource" label="Routing Info" data-id="element-72" />
                <SortableHeader field="createdAt" label="Created" data-id="element-73" />
                <SortableHeader field="status" label="Status" data-id="element-74" />
                <SortableHeader field="errorCode" label="Error Type" className="hidden lg:table-cell" data-id="element-75" />
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-76">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-77">
              {sortedAndFilteredLeads.map(lead => {
            const needsPush = lead.status === 'missing' || lead.status === 'error' || lead.status === 'pending';
            const isExpanded = expandedErrorId === lead.id && lead.status === 'error';
            return <Fragment key={lead.id} data-id="element-78">
                    <tr className={`hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-amber-50/30' : ''}`} data-id="element-79">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-80">
                        <div className="flex flex-col" data-id="element-81">
                          <span className="text-sm font-medium text-gray-900" data-id="element-82">
                            {lead.name}
                          </span>
                          <span className="text-sm text-gray-500" data-id="element-83">
                            {lead.email}
                          </span>
                          <span className="text-xs text-gray-400 mt-0.5" data-id="element-84">
                            {lead.phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-85">
                        <div className="flex flex-col" data-id="element-86">
                          <span className="text-sm text-gray-900 font-medium" data-id="element-87">
                            {lead.leadSource}
                          </span>
                          <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-88">
                            <span className="text-gray-400 mr-1" data-id="element-89">Target:</span>
                            <span className="truncate max-w-[150px]" data-id="element-90">
                              {lead.expectedDialingList}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-91">
                        {formatTime(lead.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-92">
                        {getStatusBadge(lead)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell" data-id="element-93">
                        {lead.errorCode ? <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded" data-id="element-94">
                            {lead.errorCode}
                          </span> : <span className="text-gray-300" data-id="element-95">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-96">
                        {needsPush ? <button onClick={() => onPushSingle(lead.id)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-97">
                            Push to List
                          </button> : lead.status === 'pending_verification' ? <span className="inline-flex items-center text-blue-600 text-xs font-medium" data-id="element-98">
                            <LoaderIcon className="h-3.5 w-3.5 mr-1 animate-spin" data-id="element-99" />
                            Verifying...
                          </span> : <span className="inline-flex items-center text-green-600 text-xs font-medium" data-id="element-100">
                            <CheckIcon className="h-4 w-4 mr-1" data-id="element-101" />
                            On List
                          </span>}
                      </td>
                    </tr>

                    {/* Expanded Error Row */}
                    {isExpanded && <tr className="bg-amber-50 border-l-4 border-l-amber-400" data-id="element-102">
                        <td colSpan={6} className="px-6 py-4" data-id="element-103">
                          <div className="flex items-start justify-between" data-id="element-104">
                            <div className="flex items-start space-x-3" data-id="element-105">
                              <AlertTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-106" />
                              <div data-id="element-107">
                                <div className="flex items-center space-x-2 mb-1" data-id="element-108">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-200 text-amber-900 text-xs font-mono font-medium" data-id="element-109">
                                    {lead.errorCode}
                                  </span>
                                  <span className="text-sm font-medium text-amber-900" data-id="element-110">
                                    Routing Failed
                                  </span>
                                </div>
                                <p className="text-sm text-amber-800 max-w-3xl" data-id="element-111">
                                  {lead.errorMessage || 'An unknown error occurred while routing this lead.'}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => onPushSingle(lead.id)} className="inline-flex items-center px-3 py-1.5 border border-amber-300 shadow-sm text-xs font-medium rounded text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-112">
                              <RefreshCwIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-113" />
                              Retry Push
                            </button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
          })}
            </tbody>
          </table> : <div className="text-center py-16 px-4" data-id="element-114">
            <AlertCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="element-115" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-116">
              No leads found
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-117">
              Try adjusting your search or filter criteria.
            </p>
            <button onClick={() => {
          setSourceFilter(null);
          setStatusFilter(null);
          setErrorFilter(null);
          setSearchQuery('');
        }} className="text-blue-600 hover:text-blue-800 font-medium text-sm" data-id="element-118">
              Clear all filters
            </button>
          </div>}
      </div>

      {/* Footer */}
      {sortedAndFilteredLeads.length > 0 && <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center" data-id="element-119">
          <span data-id="element-120">Showing {sortedAndFilteredLeads.length} leads</span>
        </div>}
    </div>;
}